import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";

// Whole-site on-the-fly translation. When the user picks a non-English language,
// visible text AND key attributes (placeholder, aria-label, title, alt) are translated
// via the cached /translate/ui endpoint and swapped in place. Originals are remembered
// so switching back to English restores them instantly.

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "SVG", "PATH"]);
const ATTRS = ["placeholder", "aria-label", "title", "alt"];
const RTL = new Set(["ar", "ur", "he", "fa"]);
const memCache = {}; // lang -> { orig: translated }

function loadCache(lang) {
  if (memCache[lang]) return memCache[lang];
  let obj = {};
  try { obj = JSON.parse(localStorage.getItem(`pula_tr_${lang}`) || "{}"); } catch { obj = {}; }
  memCache[lang] = obj;
  return obj;
}
function saveCache(lang) {
  try { localStorage.setItem(`pula_tr_${lang}`, JSON.stringify(memCache[lang] || {})); } catch { /* quota */ }
}

const translatable = (v) => {
  const s = (v || "").trim();
  return s.length >= 2 && /[A-Za-z]/.test(s) ? s : null;
};

export default function AutoTranslate() {
  const { language } = useSettings();

  useEffect(() => {
    document.documentElement.dir = RTL.has(language) ? "rtl" : "ltr";
    if (!language || language === "en") {
      if (sessionStorage.getItem("pula_translated") === "1") {
        sessionStorage.removeItem("pula_translated");
        window.location.reload();
      }
      return undefined;
    }
    sessionStorage.setItem("pula_translated", "1");

    const cache = loadCache(language);
    const done = new Set(Object.values(cache));  // strings already showing a translation
    const textOrig = new Map();                  // textNode -> original string
    const attrOrig = [];                          // { el, attr, orig }
    let busy = false;
    let timer = null;
    let cancelled = false;

    const inNoTranslate = (el) => {
      while (el) {
        if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
        if (el.isContentEditable) return true;
        el = el.parentElement;
      }
      return false;
    };

    const skipText = (node) => {
      let el = node.parentElement;
      while (el) {
        if (SKIP_TAGS.has(el.tagName)) return true;
        if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
        if (el.isContentEditable) return true;
        el = el.parentElement;
      }
      return false;
    };

    const collectText = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          const s = translatable(n.nodeValue);
          if (!s) return NodeFilter.FILTER_REJECT;
          if (done.has(s)) return NodeFilter.FILTER_REJECT;
          if (skipText(n)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const out = [];
      let cur;
      while ((cur = walker.nextNode())) out.push(cur);
      return out;
    };

    const collectAttrs = () => {
      const out = [];
      ATTRS.forEach((attr) => {
        document.body.querySelectorAll(`[${attr}]`).forEach((el) => {
          const s = translatable(el.getAttribute(attr));
          if (!s || done.has(s) || inNoTranslate(el)) return;
          out.push({ el, attr, val: s });
        });
      });
      return out;
    };

    const applyText = (n, s) => {
      const tr = cache[s];
      if (tr && tr !== s) {
        if (!textOrig.has(n)) textOrig.set(n, n.nodeValue);
        n.nodeValue = n.nodeValue.replace(s, tr);
      }
    };
    const applyAttr = (a) => {
      const tr = cache[a.val];
      if (tr && tr !== a.val) {
        attrOrig.push({ el: a.el, attr: a.attr, orig: a.el.getAttribute(a.attr) });
        a.el.setAttribute(a.attr, tr);
      }
    };

    const pass = async () => {
      if (cancelled || busy) return;
      busy = true;
      try {
        const applyCached = () => {
          collectText().forEach((n) => { const s = n.nodeValue.trim(); if (cache[s]) applyText(n, s); });
          collectAttrs().forEach((a) => { if (cache[a.val]) applyAttr(a); });
        };
        applyCached();
        const missing = new Set();
        collectText().forEach((n) => { const s = n.nodeValue.trim(); if (!cache[s]) missing.add(s); });
        collectAttrs().forEach((a) => { if (!cache[a.val]) missing.add(a.val); });
        const all = Array.from(missing);
        for (let i = 0; i < all.length && !cancelled; i += 80) {
          try {
            const { data } = await api.post("/translate/ui", { target_lang: language, texts: all.slice(i, i + 80) });
            const tr = data.translations || {};
            if (Object.keys(tr).length) {
              Object.entries(tr).forEach(([k, v]) => { cache[k] = v; done.add(v); });
              saveCache(language);
              applyCached();   // reveal each batch as soon as it lands
            }
          } catch { /* one batch failed — keep going */ }
        }
      } finally { busy = false; }
    };

    const schedule = () => { clearTimeout(timer); timer = setTimeout(pass, 400); };
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    pass();
    const interval = setInterval(pass, 2500); // safety net for React re-renders / route changes

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(interval);
      obs.disconnect();
      // Restore originals so switching language (or back to English) is clean.
      textOrig.forEach((orig, node) => { try { node.nodeValue = orig; } catch { /* detached */ } });
      attrOrig.forEach(({ el, attr, orig }) => { try { if (orig != null) el.setAttribute(attr, orig); } catch { /* detached */ } });
    };
  }, [language]);

  return null;
}
